import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw, Search, X } from "@fg/icons";
import { cn } from "@/lib/utils";
import { FontPicker } from "../edit/FontPicker";
import { filterRows, type PropRow, type PropTab } from "./rows";

export interface PropertiesPaneProps {
    /** Names what is selected: "Text box", "Picture", "4 objects", "Page 3", "Document". */
    title: string;
    /** Everything on the page that can be picked from the list. */
    elements: Array<{ id: string; label: string }>;
    currentElement: string | null;
    onPickElement(id: string): void;
    tab: PropTab;
    onTab(t: PropTab): void;
    /** Tabs that have nothing to show for the selection are greyed. */
    disabledTabs?: PropTab[];
    rows: PropRow[];
    /** Applies a typed value; returns a sentence when the value is refused. */
    onApply(key: string, value: string): string | void | Promise<string | void>;
    onReset(key: string): void;
    onClose(): void;
    width: number;
    onWidth(w: number): void;
}

const TABS: PropTab[] = ["Properties", "Events", "Page", "Document"];
const field = "h-7 w-full min-w-0 rounded border border-border bg-background px-1.5 text-xs outline-none focus:ring-1 focus:ring-ring disabled:opacity-60";

/**
 * The Properties window (F4): a dockable pane on the right with a header naming what is selected (and a list to pick any element of the
 * page from), tabs, a search box, a categorised or alphabetical property grid with a name column and an editor column, and a description
 * of the property in focus. Every edit applies at once, as one undo step, and a refused value says why under the row.
 */
export function PropertiesPane(p: PropertiesPaneProps) {
    const [q, setQ] = useState("");
    const [categorized, setCategorized] = useState(true);
    const [closed, setClosed] = useState<Set<string>>(new Set());
    const [focus, setFocus] = useState<string | null>(null);
    const [err, setErr] = useState<{ key: string; msg: string } | null>(null);
    const rows = useMemo(() => filterRows(p.rows, q), [p.rows, q]);
    const cats = useMemo(() => {
        const m = new Map<string, PropRow[]>();
        for (const r of (categorized ? rows : [...rows].sort((a, b) => a.label.localeCompare(b.label)))) { const k = categorized ? r.cat : ""; const l = m.get(k) ?? []; l.push(r); m.set(k, l); }
        return [...m];
    }, [rows, categorized]);
    const described = rows.find((r) => r.key === focus) ?? rows[0];
    const drag = useRef<{ x: number; w: number } | null>(null);
    useEffect(() => {
        const move = (e: PointerEvent) => { if (drag.current) p.onWidth(Math.min(560, Math.max(260, drag.current.w + (drag.current.x - e.clientX)))); };
        const up = () => { drag.current = null; };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    }, [p]);

    const apply = async (key: string, value: string) => {
        const m = await p.onApply(key, value);
        setErr(m ? { key, msg: m } : null);
    };

    return (
        <aside aria-label="Properties" data-properties-pane className="relative flex h-full shrink-0 flex-col border-l bg-card text-xs text-card-foreground" style={{ width: p.width }}>
            <div role="separator" aria-orientation="vertical" aria-label="Resize the Properties pane" title="Drag to resize" className="absolute -left-1 top-0 z-10 h-full w-2 cursor-col-resize" onPointerDown={(e) => { drag.current = { x: e.clientX, w: p.width }; e.preventDefault(); }} />
            <div className="flex items-center gap-1 border-b px-2 py-1.5">
                <b className="text-sm">Properties</b>
                <span className="ml-1 text-muted-foreground">F4</span>
                <button type="button" aria-label="Close Properties" title="Close (F4)" onClick={p.onClose} className="ml-auto rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
            </div>
            <div className="border-b px-2 py-1.5">
                <label className="sr-only" htmlFor="props-element">Element</label>
                <select id="props-element" data-props-element className={field} value={p.currentElement ?? ""} onChange={(e) => p.onPickElement(e.target.value)}>
                    {p.currentElement === null || !p.elements.some((x) => x.id === p.currentElement) ? <option value="">{p.title}</option> : null}
                    {p.elements.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
            </div>
            <div role="tablist" aria-label="Property sets" className="flex border-b">
                {TABS.map((t) => {
                    const off = p.disabledTabs?.includes(t);
                    return <button key={t} role="tab" type="button" aria-selected={p.tab === t} disabled={off} data-props-tab={t} onClick={() => p.onTab(t)} className={cn("flex-1 border-b-2 px-1 py-1.5 hover:bg-accent disabled:pointer-events-none disabled:opacity-40", p.tab === t ? "border-primary font-semibold" : "border-transparent")}>{t}</button>;
                })}
            </div>
            <div className="flex items-center gap-1 border-b px-2 py-1.5">
                <div className="relative min-w-0 flex-1">
                    <Search aria-hidden className="pointer-events-none absolute left-1.5 top-2 h-3 w-3 opacity-60" />
                    <input aria-label="Search properties" data-props-search className={cn(field, "pl-6")} placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <button type="button" aria-pressed={categorized} title="Group by category" onClick={() => setCategorized(true)} className={cn("h-7 rounded border px-1.5 hover:bg-accent", categorized && "bg-accent")}>Categories</button>
                <button type="button" aria-pressed={!categorized} title="Sort A to Z" onClick={() => setCategorized(false)} className={cn("h-7 rounded border px-1.5 hover:bg-accent", !categorized && "bg-accent")}>A–Z</button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto" data-props-grid>
                {rows.length === 0 ? <p className="p-3 text-muted-foreground">{q ? `Nothing matches “${q}”.` : p.tab === "Events" ? "Select a form field, a button or a link to give it events." : "Nothing here."}</p> : null}
                {cats.map(([cat, list]) => (
                    <section key={cat || "all"} aria-label={cat || "Properties"}>
                        {cat ? (
                            <button type="button" className="flex w-full items-center gap-1 bg-muted/60 px-2 py-1 text-left font-semibold hover:bg-muted" onClick={() => setClosed((s) => { const n = new Set(s); if (n.has(cat)) n.delete(cat); else n.add(cat); return n; })} aria-expanded={!closed.has(cat)}>
                                {closed.has(cat) ? <ChevronRight className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}{cat}
                            </button>
                        ) : null}
                        {closed.has(cat) ? null : list.map((r) => (
                            <PropEditor key={r.key} r={r} focused={focus === r.key} error={err?.key === r.key ? err.msg : null} onFocus={() => setFocus(r.key)} onApply={(v) => apply(r.key, v)} onReset={() => p.onReset(r.key)} />
                        ))}
                    </section>
                ))}
            </div>
            <div className="min-h-[3.5rem] border-t bg-muted/40 px-2 py-1.5" data-props-desc>
                {described ? <><b>{described.label}</b><p className="text-muted-foreground">{described.desc}</p></> : null}
            </div>
        </aside>
    );
}

function PropEditor({ r, focused, error, onFocus, onApply, onReset }: { r: PropRow; focused: boolean; error: string | null; onFocus(): void; onApply(v: string): void; onReset(): void }) {
    const [draft, setDraft] = useState<string | null>(null);
    const value = draft ?? r.value;
    const commit = () => { if (draft !== null && draft !== r.value) onApply(draft); setDraft(null); };
    const canReset = r.def !== undefined && r.value !== r.def && !r.readonly;
    const ro = r.readonly || r.kind === "info";
    let editor: React.ReactNode;
    switch (r.kind) {
        case "bool":
            editor = <input type="checkbox" aria-label={r.label} data-prop={r.key} checked={r.mixed ? false : value === "true"} ref={(el) => { if (el) el.indeterminate = Boolean(r.mixed); }} disabled={ro} onFocus={onFocus} onChange={(e) => onApply(e.target.checked ? "true" : "false")} />;
            break;
        case "choice":
            editor = <select aria-label={r.label} data-prop={r.key} className={field} value={r.mixed ? "" : r.value} disabled={ro} onFocus={onFocus} onChange={(e) => onApply(e.target.value)}>{r.mixed ? <option value="">(mixed)</option> : null}{(r.choices ?? []).map((c) => <option key={c} value={c}>{c}</option>)}</select>;
            break;
        case "color":
            editor = (
                <div className="flex items-center gap-1">
                    <input type="color" aria-label={`${r.label} swatch`} className="h-6 w-7 shrink-0 cursor-pointer rounded border p-0" value={/^#[0-9a-f]{6}$/i.test(r.value) ? r.value : "#ffffff"} disabled={ro} onFocus={onFocus} onChange={(e) => onApply(e.target.value)} />
                    <input aria-label={r.label} data-prop={r.key} className={field} value={value} placeholder={r.none ? "none" : ""} disabled={ro} onFocus={onFocus} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setDraft(null); e.stopPropagation(); }} />
                </div>
            );
            break;
        case "font":
            editor = <FontPicker value={r.value} onPick={(f) => onApply(f)} />;
            break;
        case "button":
            editor = (
                <div className="flex items-center gap-2">
                    <button type="button" data-prop={r.key} disabled={r.readonly} onFocus={onFocus} onClick={() => onApply("click")} className="h-7 shrink-0 rounded border px-2 hover:bg-accent disabled:opacity-50">{r.button ?? "Edit..."}</button>
                    {r.value ? <span className="min-w-0 truncate text-muted-foreground" title={r.value}>{r.value}</span> : null}
                </div>
            );
            break;
        case "multi":
            editor = <textarea aria-label={r.label} data-prop={r.key} rows={3} className={cn(field, "h-auto py-1")} value={value} disabled={ro} onFocus={onFocus} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Escape") setDraft(null); if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commit(); e.stopPropagation(); }} />;
            break;
        case "info":
            editor = <span className="block truncate py-1" title={r.value} data-prop={r.key}>{r.value}</span>;
            break;
        case "number":
            editor = (
                <div className="flex items-center gap-1">
                    <input type="number" aria-label={r.label} data-prop={r.key} className={field} min={r.min} max={r.max} step={r.decimals ? 1 / 10 ** r.decimals : 1} value={value} disabled={ro} onFocus={onFocus} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setDraft(null); e.stopPropagation(); }} />
                    {r.suffix ? <span className="shrink-0 text-muted-foreground">{r.suffix.trim()}</span> : null}
                </div>
            );
            break;
        default:
            editor = <input aria-label={r.label} data-prop={r.key} className={field} value={r.mixed && draft === null ? "" : value} placeholder={r.mixed ? "(mixed)" : ""} disabled={ro} onFocus={onFocus} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setDraft(null); e.stopPropagation(); }} />;
    }
    return (
        <div className={cn("border-b border-border/50 px-2 py-1", focused && "bg-accent/40")} data-prop-row={r.key} onClick={onFocus}>
            <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)_auto] items-start gap-2">
                <label className="truncate pt-1.5" title={r.label}>{r.label}</label>
                <div className="min-w-0 pt-0.5">{editor}</div>
                <button type="button" aria-label={`Reset ${r.label}`} title="Put the usual value back" disabled={!canReset} onClick={onReset} className={cn("mt-0.5 rounded p-1 hover:bg-accent", !canReset && "invisible")}><RotateCcw className="h-3 w-3" /></button>
            </div>
            {r.note ? <p className={cn("mt-0.5 pl-1", r.note.warn ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>{r.note.text}</p> : null}
            {error ? <p role="alert" className="mt-0.5 pl-1 text-destructive">{error}</p> : null}
        </div>
    );
}
